import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams, Outlet } from 'react-router-dom';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@mui/material/Button';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LogoutIcon from '@mui/icons-material/Logout';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SettingsIcon from '@mui/icons-material/Settings';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoIcon from '@mui/icons-material/Info';
import TranslateIcon from '@mui/icons-material/Translate';
import { getAuth, signOut } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import { useAuth } from '../auth/AuthContext';

const BRAND_COLOR = '#0071e3';
const drawerWidth = 260;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  backgroundColor: BRAND_COLOR,
  ...theme.mixins.toolbar,
}));

type ManagementMode = 'company-list' | 'company' | 'store';

interface ManagementLayoutProps {
  mode: ManagementMode;
}

const ManagementLayout: React.FC<ManagementLayoutProps> = ({ mode }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { companyId, storeId } = useParams<{ companyId: string; storeId: string }>();
  const [open, setOpenState] = useState(() => {
    const saved = localStorage.getItem('sidebar_open');
    if (saved !== null) {
      return saved === 'true';
    }
    // 初期値をlocalStorageにも書き込む
    localStorage.setItem('sidebar_open', 'true');
    return true;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const setOpen = (value: boolean) => {
    setOpenState(value);
    localStorage.setItem('sidebar_open', value ? 'true' : 'false');
  };
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { managementAccount, logoutManagement, isSuperadmin } = useManagementAuth();
  const { isAdmin } = useAuth();

  const handleLogout = useCallback(async () => {
    logoutManagement();
    try {
      await signOut(getAuth(getApp()));
    } catch {
      // Firebase signOut failure is non-critical
    }
    navigate('/login');
  }, [logoutManagement, navigate]);

  // モードに応じたメニュー項目
  const getMenuItems = () => {
    if (mode === 'company-list') {
      // 企業/店舗一覧画面のメニュー
      return [
        { text: '企業・店舗一覧', icon: <FormatListBulletedIcon />, path: '/management' },
        ...(isAdmin ? [
          { text: '承認管理', icon: <GavelIcon />, path: '/management/approvals' },
          { text: '運営設定', icon: <SettingsIcon />, path: '/management/settings' },
          { text: '即給連携管理', icon: <ReceiptIcon />, path: '/admin/sokyu-config' },
        ] : []),
        ...(isSuperadmin ? [
          { text: '正準スキル辞書', icon: <TranslateIcon />, path: '/admin/canonical-skills' },
        ] : []),
        { text: 'ログアウト', icon: <LogoutIcon />, path: '__logout__' },
      ];
    }

    if (mode === 'company') {
      // 企業管理画面のメニュー
      return [
        { text: '企業情報', icon: <BusinessIcon />, path: `/management/company/${companyId}` },
        { text: '店舗一覧', icon: <StoreIcon />, path: `/management/company/${companyId}/stores` },
        { text: '利用明細', icon: <ReceiptIcon />, path: `/management/company/${companyId}/billing` },
        { text: '労働者名簿', icon: <PeopleIcon />, path: `/management/company/${companyId}/workers` },
        { text: 'メール受信設定', icon: <EmailIcon />, path: `/management/company/${companyId}/email-settings` },
        { text: 'アカウント情報', icon: <PersonIcon />, path: `/management/company/${companyId}/account` },
        { text: 'ログアウト', icon: <LogoutIcon />, path: '__logout__' },
      ];
    }

    if (mode === 'store') {
      // 店舗詳細画面のメニュー
      return [
        { text: '店舗情報', icon: <InfoIcon />, path: `/management/company/${companyId}/store/${storeId}` },
        { text: 'ログアウト', icon: <LogoutIcon />, path: '__logout__' },
      ];
    }

    return [];
  };

  const menuItems = getMenuItems();

  const drawerContent = (
    <>
      {isMobile ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, ...theme.mixins.toolbar }}>
          <Box
            onClick={() => setMobileOpen(false)}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', color: 'text.primary', px: 1 }}
          >
            <CloseIcon style={{ width: 26, height: 26 }} />
            <Typography sx={{ fontSize: '0.5rem', color: 'text.primary', lineHeight: 1, mt: '2px' }}>閉じる</Typography>
          </Box>
        </Box>
      ) : (
        <DrawerHeader>
          {open && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'flex-start', pl: 2 }}>
              <img src="/recrew_logo_C-3.png" alt="ReCrew Logo" style={{ height: '32px', width: 'auto' }} />
            </Box>
          )}
          <IconButton onClick={() => setOpen(!open)} sx={{ color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ChevronLeftIcon sx={{ fontSize: '20px', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }} />
              <ChevronLeftIcon sx={{ fontSize: '20px', ml: '-10px', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }} />
            </Box>
          </IconButton>
        </DrawerHeader>
      )}

      {!isMobile && <Divider />}

      {/* 切替ボタン */}
      <Box sx={{ p: open || isMobile ? 2 : 1, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
        {mode === 'store' ? (
          <Button
            variant="outlined"
            startIcon={(open || isMobile) ? <SwapHorizIcon /> : undefined}
            onClick={() => navigate(`/management/company/${companyId}/stores`)}
            size="small"
            fullWidth={open || isMobile}
            sx={{
              borderColor: BRAND_COLOR,
              color: BRAND_COLOR,
              minWidth: open || isMobile ? undefined : 40,
              px: open || isMobile ? 2 : 0,
            }}
          >
            {(open || isMobile) ? '店舗一覧' : <SwapHorizIcon />}
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              startIcon={(open || isMobile) ? <SwapHorizIcon /> : undefined}
              onClick={() => navigate('/')}
              size="small"
              fullWidth={open || isMobile}
              sx={{
                borderColor: BRAND_COLOR,
                color: BRAND_COLOR,
                minWidth: open || isMobile ? undefined : 40,
                px: open || isMobile ? 2 : 0,
              }}
            >
              {(open || isMobile) ? '店舗管理' : <SwapHorizIcon />}
            </Button>
            {mode === 'company' && isAdmin && (
              <Button
                variant="outlined"
                startIcon={(open || isMobile) ? <SwapHorizIcon /> : undefined}
                onClick={() => navigate('/management')}
                size="small"
                fullWidth={open || isMobile}
                sx={{
                  borderColor: BRAND_COLOR,
                  color: BRAND_COLOR,
                  minWidth: open || isMobile ? undefined : 40,
                  px: open || isMobile ? 2 : 0,
                }}
              >
                {(open || isMobile) ? '運営メニュー' : <SwapHorizIcon />}
              </Button>
            )}
            {mode === 'company-list' && managementAccount?.companies?.[0]?.id && (
              <Button
                variant="outlined"
                startIcon={(open || isMobile) ? <SwapHorizIcon /> : undefined}
                onClick={() => navigate(`/management/company/${managementAccount.companies[0].id}`)}
                size="small"
                fullWidth={open || isMobile}
                sx={{
                  borderColor: BRAND_COLOR,
                  color: BRAND_COLOR,
                  minWidth: open || isMobile ? undefined : 40,
                  px: open || isMobile ? 2 : 0,
                }}
              >
                {(open || isMobile) ? '企業管理' : <SwapHorizIcon />}
              </Button>
            )}
          </>
        )}
      </Box>

      {/* ユーザー名表示 */}
      {(open || isMobile) && managementAccount && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {managementAccount.name}
          </Typography>
        </Box>
      )}

      <Divider />

      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: (open || isMobile) ? 'initial' : 'center',
                px: 2.5,
              }}
              selected={location.pathname === item.path}
              onClick={() => {
                if (item.path === '__logout__') {
                  handleLogout();
                } else {
                  navigate(item.path);
                }
                if (isMobile) setMobileOpen(false);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: (open || isMobile) ? 3 : 'auto',
                  justifyContent: 'center',
                  color: location.pathname === item.path ? BRAND_COLOR : 'inherit'
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  opacity: (open || isMobile) ? 1 : 0,
                  color: location.pathname === item.path ? BRAND_COLOR : 'inherit'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  const getSidebarWidth = () => {
    if (isMobile) return 0;
    if (open) return drawerWidth;
    return parseInt(theme.spacing(8));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* モバイルヘッダー */}
      {isMobile && (
        <AppBar
          position="fixed"
          open={false}
          color="inherit"
          elevation={0}
          sx={{
            backgroundColor: 'white',
            borderBottom: '1px solid #e0e0e0',
            display: 'block',
            zIndex: mobileOpen ? theme.zIndex.drawer - 1 : theme.zIndex.appBar,
          }}
        >
          <Toolbar sx={{ backgroundColor: 'white', minHeight: '65px !important', px: 1.5 }}>
            <Box
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', color: 'text.primary', mr: 1, minWidth: 52, px: 1 }}
            >
              <FormatListBulletedIcon style={{ width: 26, height: 26 }} />
              <Typography sx={{ fontSize: '0.5rem', color: 'text.primary', lineHeight: 1, mt: '2px' }}>メニュー</Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <img src="/recrew_logo_C-3.png" alt="ReCrew Logo" style={{ height: '28px', width: 'auto' }} />
          </Toolbar>
        </AppBar>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {/* デスクトップドロワー */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            open={open}
            sx={{ '& .MuiDrawer-paper': { backgroundColor: 'white' } }}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* モバイルドロワー */}
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 320, backgroundColor: 'white' },
          }}
        >
          {drawerContent}
        </MuiDrawer>

        {/* メインコンテンツ */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            pb: { xs: 3, sm: 3 },
            width: `calc(100% - ${isMobile ? 0 : getSidebarWidth()}px)`,
            mt: { xs: '65px', sm: 0 },
            backgroundColor: '#f8f8f9',
            minHeight: 'calc(100vh - 64px)',
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default ManagementLayout;
